import { FormControl } from '@angular/forms';
import { MAX_FILE_SIZE, MAX_FILE_SIZE_HUMAN_READABLE } from '@app/constants/file';

/**
 * An Uploadable represents the information required to upload something.
 *
 * The child class must call the afterConstruct method in its constructor
 * for file validation to happen
 *
 * Ex: CompanyDocumentUploadable represents the information required to upload a CompanyDocument
 * including a file, tags, and a name.
 */
export abstract class Uploadable {
    file: File;
    status: 'pending' | 'uploading' | 'failed' | 'complete' = 'pending';
    /**
     * We are using a form control so that Angular Material can show our invalid states using mat-error
     * We don't love this approach.
     */
    nameFormControl = new FormControl('');
    isTooLarge = false;
    hasWrongFileType = false;
    maxFileSize = MAX_FILE_SIZE_HUMAN_READABLE;

    /**
     * The mime types of the file. Validated when the user inputs a file.
     *
     * Ex: ['application/pdf'] or ['application/pdf', 'image/png']
     */
    protected abstract readonly allowedMimeTypes: string[];

    /**
     *  The file extensions to be used for validation.
     *
     *  Ex: ['pdf'] or ['pdf, 'csv']
     */
    protected abstract readonly allowedExtensions: string[];

    constructor(file: File) {
        this.file = file;
    }

    /**
     * Defines checks specific to this type of uploadable
     *
     * Ex: Is the name long enough. Is it a duplicate. Etc.
     */
    protected abstract checkIsReadyToUpload(): boolean;

    /**
     * Set errors to be shown in the UI if the uploadable is not ready to be uploaded
     *
     * Ex: Name too long
     */
    protected abstract setErrors(): void;

    /**
     * This method determines if an uploadable is in a state where it would okay to upload
     */
    isReadyToUpload(): boolean {
        if (this.status !== 'pending' || this.hasInvalidFile() || !this.getUploadableName()) {
            return false;
        }

        return this.checkIsReadyToUpload();
    }

    getUploadableName(): string {
        return this.nameFormControl.value.trim();
    }

    /**
     * Is the file itself invalid
     *
     * This check is independent of checks on the Uploadable
     * If a file is not valid, the Uploadable can never be valid
     */
    hasInvalidFile(): boolean {
        return this.isTooLarge || this.hasWrongFileType;
    }

    /**
     * Is the file itself valid
     */
    hasValidFile(): boolean {
        return !this.hasInvalidFile();
    }

    /**
     * If the uploadable is not ready to upload, set any errors for the UI
     *
     * This method is run after validation
     */
    updateErrors(): void {
        if (this.isReadyToUpload()) {
            this.nameFormControl.setErrors(null);

            return;
        }

        this.setErrors();
    }

    /**
     * This function needs to be called by the child class
     * to perform validation
     *
     * This validation cannot be done in the abstract constructor
     * because the child classes properties are not yet initialized
     */
    protected afterConstruct(): void {
        this.validateFileType();
        this.validateFileSize();

        this.setNameFromFile();

        this.nameFormControl.markAsTouched();
    }

    /**
     * Checks that the mimetype matches the file type
     *
     * This is a surface level check. The file type is derived from the extension.
     * Proper validation happens on the server.
     */
    protected validateFileType(): void {
        this.hasWrongFileType = !this.allowedMimeTypes.some((type) => type === this.file.type);
    }

    /**
     * Checks that the file size is not too big
     *
     * If your requirements are to have a smaller file, override this method
     */
    protected validateFileSize(): void {
        this.isTooLarge = this.file.size > MAX_FILE_SIZE;
    }

    /**
     * Sets the value of the form control which is used to house the name
     *
     * If the file is invalid, the name is set to be empty.
     *
     *Ex: "activity_report.pdf" becomes "activity_report"
     */
    protected setNameFromFile(): void {
        // if the file is invalid, we leave the empty string name
        // this avoids duplicate filename collisions
        if (this.hasInvalidFile()) {
            return;
        }

        const indexOfExtension: number | undefined = this.allowedExtensions
            .map((ext) => this.file.name.lastIndexOf('.' + ext))
            .find((index) => index !== -1);

        // if no extension, use filename as is
        if (indexOfExtension === undefined) {
            this.nameFormControl.setValue(this.file.name);
        }

        this.nameFormControl.setValue(this.file.name.substr(0, indexOfExtension));
    }
}
